import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  FileText, 
  Search, 
  Download, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  AlertCircle, 
  Check, 
  Loader2, 
  PackageOpen, 
  ChevronDown, 
  ChevronUp,
  RotateCcw
} from "lucide-react";
import { db } from "../firebase/config";
import { collection, onSnapshot, query, orderBy, doc, updateDoc, addDoc, where } from "firebase/firestore";
import ResolveTicketModal from "../components/modals/ResolveTicketModal";
import TicketChat from "../components/ui/TicketChat";
import { useAuth } from "../context/AuthContext";
import { logSecurityEvent } from "../services/AuditLogger";

export default function Support() {
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("Incomplete");
  const [toast, setToast] = useState({ message: "", type: "" });
  const [resolvingTicket, setResolvingTicket] = useState(null);
  const [submittingResolve, setSubmittingResolve] = useState(false);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  
  const { currentUser } = useAuth();
  const [currentUserName, setCurrentUserName] = useState("");
  const [currentUserRoles, setCurrentUserRoles] = useState([]);

  useEffect(() => {
    if (!currentUser || !currentUser.email) return;
    const q = query(collection(db, "users"), where("email", "==", currentUser.email));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const userData = snapshot.docs[0].data();
        setCurrentUserName(userData.name || currentUser.email.split("@")[0]);
        setCurrentUserRoles(userData.roles || []);
      } else {
        setCurrentUserName(currentUser.email.split("@")[0]);
        setCurrentUserRoles([]);
      }
    }, (err) => {
      console.error("Error loading user profile in Support:", err);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const handleToggleRow = async (ticket) => {
    const isExpanded = expandedTicketId === ticket.id;
    if (!isExpanded) {
      setExpandedTicketId(ticket.id);
      // Clear unread flag if active developer has not read last message
      if (ticket.lastMessageAt && currentUser && !(ticket.lastMessageReadBy || []).includes(currentUser.uid)) {
        try {
          await updateDoc(doc(db, "tickets", ticket.id), {
            lastMessageReadBy: [...(ticket.lastMessageReadBy || []), currentUser.uid]
          });
        } catch (err) {
          console.error("Error updating lastMessageReadBy in Support:", err);
        }
      }
    } else {
      setExpandedTicketId(null);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 4000);
  };

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, "tickets"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      setTickets(list);
      setLoading(false);
    }, (err) => {
      console.error("Error subscribing to tickets:", err);
      showToast("Failed to load support tickets.", "error");
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Handle deep-linking from notifications (?ticketId=... or ?ticketNumber=...)
  useEffect(() => {
    const targetId = searchParams.get("ticketId");
    const targetNum = searchParams.get("ticketNumber");
    if (!targetId && !targetNum) return;
    if (tickets.length === 0) return;

    const matched = tickets.find(t => 
      (targetId && t.id === targetId) || 
      (targetNum && t.ticketNumber && t.ticketNumber.toUpperCase() === targetNum.toUpperCase())
    );

    if (matched) {
      if (matched.status === "Complete" && statusFilter === "Incomplete") {
        setStatusFilter("All");
      }
      setExpandedTicketId(matched.id);
      if (matched.lastMessageAt && currentUser && !(matched.lastMessageReadBy || []).includes(currentUser.uid)) {
        updateDoc(doc(db, "tickets", matched.id), {
          lastMessageReadBy: [...(matched.lastMessageReadBy || []), currentUser.uid]
        }).catch(console.error);
      }
      setTimeout(() => {
        const el = document.getElementById(`ticket-${matched.id}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
  }, [searchParams, tickets, statusFilter, currentUser]);

  const handleResolveTicket = async (ticket, resolutionNote) => {
    try {
      setSubmittingResolve(true);
      // 1. Update ticket status to Complete and store resolutionNote
      await updateDoc(doc(db, "tickets", ticket.id), {
        status: "Complete",
        resolutionNote
      });

      // 2. Add real-time notification to the user who filed it
      await addDoc(collection(db, "notifications"), {
        key: `ticket_solved_${ticket.id}`,
        message: `Developer Alert: Your ticket ${ticket.ticketNumber} (${ticket.type}) has been solved.`,
        type: "ticket_solved",
        ticketId: ticket.id,
        ticketNumber: ticket.ticketNumber,
        path: `/profile?tab=tickets&ticketId=${ticket.id}&ticketNumber=${ticket.ticketNumber}`,
        targetUser: ticket.createdBy, // Matches display name
        createdAt: new Date().toISOString(),
        readBy: []
      });

      showToast(`Ticket ${ticket.ticketNumber} marked as Complete and user notified!`);
      setResolvingTicket(null);
    } catch (err) {
      console.error("Error completing ticket:", err);
      showToast("Failed to complete ticket.", "error");
    } finally {
      setSubmittingResolve(false);
    }
  };
  const handleSetWaiting = async (ticket) => {
    try {
      const docRef = doc(db, "tickets", ticket.id);
      await updateDoc(docRef, { status: "Waiting" });
      showToast(`Ticket ${ticket.ticketNumber} marked as Waiting.`);
    } catch (err) {
      console.error("Error setting ticket status to Waiting:", err);
      showToast("Failed to set ticket status to Waiting.", "error");
    }
  };

  const handleReopenTicket = async (ticket) => {
    try {
      await updateDoc(doc(db, "tickets", ticket.id), {
        status: "Open",
        reopenedAt: new Date().toISOString(),
        reopenedBy: currentUserName || currentUser?.email || "Support Agent",
        lastAction: "Reopened by Developer"
      });

      // Add real-time notification to the user who filed it
      await addDoc(collection(db, "notifications"), {
        key: `ticket_reopened_${ticket.id}_${Date.now()}`,
        message: `Developer Alert: Ticket ${ticket.ticketNumber} (${ticket.type}) has been reopened back to Open.`,
        type: "ticket_reopened",
        ticketId: ticket.id,
        ticketNumber: ticket.ticketNumber,
        path: `/profile?tab=tickets&ticketId=${ticket.id}&ticketNumber=${ticket.ticketNumber}`,
        targetUser: ticket.createdBy,
        createdAt: new Date().toISOString(),
        readBy: []
      });

      // Record tamper-resistant compliance audit event
      await logSecurityEvent({
        action: "TICKET_REOPENED",
        category: "DATA_MODIFICATION",
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        resource: "tickets",
        details: {
          ticketId: ticket.id,
          ticketNumber: ticket.ticketNumber,
          previousStatus: ticket.status,
          reopenedBy: currentUserName || currentUser?.email
        },
        severity: "INFO"
      });

      showToast(`Ticket ${ticket.ticketNumber} reopened back to Open.`);
    } catch (err) {
      console.error("Error reopening ticket:", err);
      showToast("Failed to reopen ticket.", "error");
    }
  };

  const getTicketStatusStyle = (status) => {
    switch (status) {
      case "Complete":
        return {
          backgroundColor: "rgba(76, 175, 80, 0.15)",
          color: "var(--success)",
          border: "1px solid var(--success)"
        };
      case "Waiting":
        return {
          backgroundColor: "rgba(255, 152, 0, 0.15)",
          color: "#ff9800",
          border: "1px solid #ff9800"
        };
      case "Responded":
        return {
          backgroundColor: "rgba(33, 150, 243, 0.15)",
          color: "#2196f3",
          border: "1px solid #2196f3"
        };
      case "Open":
      default:
        return {
          backgroundColor: "rgba(239, 68, 68, 0.15)",
          color: "#ef4444",
          border: "1px solid #ef4444"
        };
    }
  };

  // Filter logic
  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.createdBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesType = typeFilter === "All" || ticket.type === typeFilter;
    
    let matchesStatus = true;
    if (statusFilter === "Incomplete") {
      matchesStatus = ticket.status !== "Complete";
    } else if (statusFilter !== "All") {
      matchesStatus = ticket.status === statusFilter;
    }

    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="page-header-info">
          <h1>Support Ticket Queue</h1>
          <p>Review bug reports, feature requests, and general feedback submitted by users.</p>
        </div>
      </div>

      {toast.message && (
        <div 
          className={`alert ${toast.type === "error" ? "alert-danger" : "alert-success"}`} 
          style={{ 
            position: "fixed", 
            top: "20px", 
            right: "20px", 
            zIndex: 1000, 
            boxShadow: "var(--shadow-lg)",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          {toast.type === "error" ? <AlertCircle size={18} /> : <Check size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        <div className="proc-search-box" style={{ flex: 2, minWidth: "250px" }}>
          <Search size={18} className="proc-search-icon" />
          <input
            type="text"
            className="form-input proc-search-input"
            placeholder="Search tickets by ticket #, submitter, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ margin: 0 }}
          />
        </div>

        <div style={{ flex: 1, minWidth: "150px" }}>
          <select 
            className="form-input" 
            style={{ margin: 0 }}
            value={typeFilter} 
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="All">All Types</option>
            <option value="Bug">Bug Report</option>
            <option value="Feature Request">Feature Request</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div style={{ flex: 1, minWidth: "150px" }}>
          <select 
            className="form-input" 
            style={{ margin: 0 }}
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Statuses</option>
            <option value="Incomplete">Incomplete</option>
            <option value="Open">Open</option>
            <option value="Waiting">Waiting</option>
            <option value="Responded">Responded</option>
            <option value="Complete">Complete</option>
          </select>
        </div>
      </div>

      {/* Tickets List Table */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div className="proc-loading" style={{ padding: "3rem" }}>
            <Loader2 className="proc-spinner" size={36} />
            <span>Loading Support Tickets...</span>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="proc-empty" style={{ padding: "3rem" }}>
            <PackageOpen size={48} className="proc-empty-icon" />
            <h3>No Support Tickets</h3>
            <p>No tickets were found matching the selected filters.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="proc-table">
              <thead>
                <tr>
                  <th style={{ paddingLeft: "1.5rem", width: "120px" }}>Ticket #</th>
                  <th style={{ width: "120px" }}>Created At</th>
                  <th style={{ width: "140px" }}>Submitter</th>
                  <th style={{ width: "140px" }}>Type</th>
                  <th>Description</th>
                  <th style={{ width: "120px" }}>Attachment</th>
                  <th style={{ width: "100px" }}>Status</th>
                  <th className="text-center" style={{ width: "130px", paddingRight: "1.5rem" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket) => {
                  const isExpanded = expandedTicketId === ticket.id;
                  return (
                    <React.Fragment key={ticket.id}>
                      <tr id={`ticket-${ticket.id}`} style={{ borderBottom: isExpanded ? "none" : "1px solid var(--border-color)" }}>
                        <td style={{ paddingLeft: "1.5rem" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <button 
                              type="button"
                              onClick={() => handleToggleRow(ticket)}
                              style={{ background: "none", border: "none", color: "var(--text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", padding: "0.2rem" }}
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            <span className="sku-badge" style={{ margin: 0 }}>{ticket.ticketNumber}</span>
                            {ticket.lastMessageAt && ticket.lastMessageSender !== currentUserName && currentUser && !(ticket.lastMessageReadBy || []).includes(currentUser.uid) && (
                              <span 
                                className="badge badge-info" 
                                style={{ 
                                  fontSize: "0.65rem", 
                                  padding: "0.1rem 0.35rem", 
                                  margin: 0,
                                  backgroundColor: "var(--primary)",
                                  color: "#ffffff"
                                }}
                              >
                                New Chat
                              </span>
                            )}
                          </div>
                        </td>
                    <td>
                      {ticket.createdAt 
                        ? new Date(ticket.createdAt).toLocaleDateString() + " " + new Date(ticket.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                        : "N/A"}
                    </td>
                    <td style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                      {ticket.createdBy}
                    </td>
                    <td>
                      <span 
                        className={`badge ${
                          ticket.type === "Bug" 
                            ? "badge-danger" 
                            : ticket.type === "Feature Request" 
                            ? "badge-info" 
                            : "badge-secondary"
                        }`}
                      >
                        {ticket.type}
                      </span>
                    </td>
                    <td style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem", lineHeight: 1.4 }}>
                      {ticket.description}
                    </td>
                    <td>
                      {(() => {
                        const attList = ticket.attachments && ticket.attachments.length > 0 
                          ? ticket.attachments 
                          : (ticket.attachment ? [ticket.attachment] : []);
                        if (attList.length === 0) {
                          return <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontStyle: "italic" }}>None</span>;
                        }
                        return (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
                            {attList.map((att, attIdx) => (
                              <a
                                key={attIdx}
                                href={att.data}
                                download={att.name}
                                className="badge badge-info"
                                style={{ 
                                  display: "inline-flex", 
                                  alignItems: "center", 
                                  gap: "0.25rem", 
                                  textDecoration: "none", 
                                  color: "var(--primary)",
                                  border: "1px solid var(--primary)",
                                  backgroundColor: "rgba(0,0,0,0.1)",
                                  cursor: "pointer",
                                  fontSize: "0.75rem",
                                  padding: "0.15rem 0.35rem"
                                }}
                                title={`Click to Download ${att.name}`}
                              >
                                <Download size={10} /> {attList.length === 1 ? "Doc" : `Doc ${attIdx + 1}`}
                              </a>
                            ))}
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <span 
                        className="badge" 
                        style={{ 
                          margin: 0,
                          ...getTicketStatusStyle(ticket.status)
                        }}
                      >
                        {ticket.status || "Open"}
                      </span>
                    </td>
                    <td className="text-center" style={{ paddingRight: "1.5rem" }}>
                      {ticket.status !== "Complete" ? (
                        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ display: "flex", alignItems: "center", gap: "0.25rem", padding: "0.4rem 0.6rem" }}
                            onClick={() => setResolvingTicket(ticket)}
                          >
                            <CheckCircle size={12} className="text-success" /> Complete
                          </button>
                          {ticket.status !== "Waiting" && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              style={{ display: "flex", alignItems: "center", gap: "0.25rem", padding: "0.4rem 0.6rem" }}
                              onClick={() => handleSetWaiting(ticket)}
                            >
                              <Clock size={12} className="text-warning" /> Waiting
                            </button>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", justifyContent: "center" }}>
                          <span style={{ fontSize: "0.85rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                            <Check size={14} className="text-success" /> Resolved
                          </span>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", padding: "0.3rem 0.55rem", fontSize: "0.75rem" }}
                            onClick={() => handleReopenTicket(ticket)}
                            title="Reopen ticket back to Open status"
                          >
                            <RotateCcw size={12} className="text-warning" /> Reopen
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                    {isExpanded && (
                      <tr key={`${ticket.id}-expanded`}>
                        <td colSpan={8} style={{ padding: "1.5rem", backgroundColor: "rgba(0, 0, 0, 0.25)", borderBottom: "1px solid var(--border-color)" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                            {ticket.status === "Complete" && (
                              <div>
                                <label className="form-label" style={{ color: "var(--success)", fontSize: "0.8rem", marginBottom: "0.25rem", display: "block" }}>
                                  Developer Resolution Note
                                </label>
                                <div style={{ 
                                  backgroundColor: "rgba(76, 175, 80, 0.05)", 
                                  border: "1px solid rgba(76, 175, 80, 0.2)", 
                                  padding: "0.75rem 1rem", 
                                  borderRadius: "6px",
                                  fontSize: "0.9rem",
                                  color: "var(--text-primary)"
                                }}>
                                  {ticket.resolutionNote || "Ticket completed successfully."}
                                </div>
                              </div>
                            )}
                            
                            {(() => {
                              const attList = ticket.attachments && ticket.attachments.length > 0
                                ? ticket.attachments
                                : (ticket.attachment ? [ticket.attachment] : []);
                              if (attList.length === 0) return null;
                              return (
                                <div>
                                  <label className="form-label" style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "0.35rem", display: "block" }}>
                                    Attachments ({attList.length})
                                  </label>
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                                    {attList.map((att, idx) => (
                                      <a
                                        key={idx}
                                        href={att.data}
                                        download={att.name}
                                        className="badge badge-info"
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "0.35rem",
                                          textDecoration: "none",
                                          color: "var(--primary)",
                                          border: "1px solid var(--primary)",
                                          backgroundColor: "rgba(0,0,0,0.1)",
                                          cursor: "pointer",
                                          padding: "0.35rem 0.6rem",
                                          fontSize: "0.8rem"
                                        }}
                                        title={`Download ${att.name}`}
                                      >
                                        <Download size={12} /> {att.name} {att.size ? `(${(att.size / 1024).toFixed(0)} KB)` : ""}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                            
                            <TicketChat 
                              ticket={ticket} 
                              currentUserName={currentUserName} 
                              currentUserRoles={currentUserRoles} 
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )})}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ResolveTicketModal
        isOpen={!!resolvingTicket}
        onClose={() => setResolvingTicket(null)}
        ticketNumber={resolvingTicket?.ticketNumber}
        onResolve={(note) => handleResolveTicket(resolvingTicket, note)}
        submitting={submittingResolve}
      />
    </div>
  );
}
